import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Quote, Star, ZoomIn } from "lucide-react";
import { api, photoSrc } from "@/lib/api";
import { useSiteContent } from "@/lib/content";

export function openGalleryPhoto(photoId) {
  window.dispatchEvent(new CustomEvent("open-gallery-photo", { detail: { photoId } }));
}

export default function Testimonials() {
  const content = useSiteContent();
  const list = content?.testimonials || [];
  const [index, setIndex] = useState(0);
  const [photos, setPhotos] = useState({});

  useEffect(() => {
    api
      .get("/albums")
      .then((r) => {
        const map = {};
        r.data.forEach((a) => a.photos.forEach((p) => (map[p.id] = { ...p, albumName: a.name })));
        setPhotos(map);
      })
      .catch(() => setPhotos({}));
  }, []);

  useEffect(() => {
    if (list.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % list.length), 9000);
    return () => clearInterval(t);
  }, [list.length]);

  if (list.length === 0) return null;
  const current = list[index % list.length];
  const linked = current.photo_id ? photos[current.photo_id] : null;

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative mx-auto max-w-4xl px-4 py-20 sm:px-8 sm:py-24">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="text-center"
      >
        <p className="font-script text-2xl text-gold">Sweet words</p>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-wine">
          {content?.text?.testimonials_heading || "Kind Words from Customers"}
        </h2>

        <div className="relative mt-10 rounded-[2rem] border border-gold/40 bg-white/80 px-6 py-12 shadow-[0_24px_70px_-24px_rgba(123,45,78,0.35)] backdrop-blur sm:px-16">
          <Quote className="absolute -top-5 left-1/2 h-10 w-10 -translate-x-1/2 rotate-180 rounded-full bg-gold p-2.5 text-white shadow-lg" />
          <div className="mb-5 flex justify-center gap-1" aria-hidden>
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-gold text-gold" />
            ))}
          </div>

          <div className="min-h-[280px] sm:min-h-[220px]">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={index % list.length}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid="testimonial-quote"
              >
                <p className="font-serif text-xl sm:text-2xl italic leading-relaxed text-burgundy">“{current.text}”</p>
                <footer className="mt-5 flex flex-col items-center gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-plum">{current.name}</span>
                    <span className="text-plum/50"> · {current.occasion}</span>
                  </div>
                  {linked && (
                    <button
                      type="button"
                      onClick={() => openGalleryPhoto(linked.id)}
                      data-testid="testimonial-photo-link"
                      className="group inline-flex items-center gap-3 rounded-full border border-gold/40 bg-blush/70 py-1.5 pl-1.5 pr-4 text-xs font-medium text-wine transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-golddeep"
                    >
                      <span className="relative h-11 w-11 overflow-hidden rounded-full border border-gold/50">
                        <img src={photoSrc(linked.url)} alt={linked.caption || "The cake from this review"} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        <span className="absolute inset-0 flex items-center justify-center bg-burgundy/0 opacity-0 transition-all duration-300 group-hover:bg-burgundy/40 group-hover:opacity-100">
                          <ZoomIn className="h-4 w-4 text-white" />
                        </span>
                      </span>
                      See the cake they loved
                    </button>
                  )}
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>

          <div className="mt-8 flex justify-center gap-2.5" data-testid="testimonial-dots">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Show testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-2 rounded-full transition-all duration-500 ${
                  i === index % list.length ? "w-7 bg-gold" : "w-2 bg-burgundy/20 hover:bg-gold/50"
                }`}
              />
            ))}
          </div>
        </div>

        <p className="mt-5 text-xs italic text-plum/50">Real recommendations from my Facebook page — chosen for special moments, again &amp; again.</p>
      </motion.div>
    </section>
  );
}
